import { FormControl } from '@angular/forms';
import {
  ImpressionSection,
  readImpressionsSection
} from 'src/app/shared/impressions/impressions-sections.util';

export interface NarrativeSection {
  label: string;
  html: string;
  isLongEnoughToClamp: boolean;
}

export interface PlanTextRow {
  label: string;
  value: string;
  isLongEnoughToClamp: boolean;
}

export interface TreatmentGroup {
  label: string;
  control: FormControl;
}

const VISIBLE_CHARACTERS_THAT_FIT_BEFORE_CLAMPING = 750;

const IMPRESSION_FIELDS: { label: string; section: ImpressionSection }[] = [
  { label: 'Interpretation', section: 'interpretation' },
  { label: 'Risk factors', section: 'riskFactors' }
];

const TREATMENT_FIELDS = [
  { label: 'OTC Products', key: 'recommendedEyeDrops' },
  { label: 'Rx', key: 'recommendedRxEyeDrops' },
  { label: 'Procedures', key: 'recommendedProcedures' },
  { label: 'Exercises', key: 'recommendedExercises' }
];

const PLAN_TEXT_FIELDS = [
  { label: 'Plan', key: 'recommendedPlanByDoctor' },
  { label: 'Goals', key: 'goalsForPatient' },
  { label: 'Performed today', key: 'treatmentPerformed' }
];

export function buildNarrativeSections(impressions: string): NarrativeSection[] {
  return IMPRESSION_FIELDS.map(impressionField => {
    const html = readImpressionsSection(impressions, impressionField.section);
    return {
      label: impressionField.label,
      html: html,
      isLongEnoughToClamp:
        countVisibleCharacters(html) > VISIBLE_CHARACTERS_THAT_FIT_BEFORE_CLAMPING
    };
  }).filter(narrativeSection => !!narrativeSection.html);
}

export function buildTreatmentGroups(assessmentForm: any): TreatmentGroup[] {
  if (!assessmentForm) {
    return [];
  }
  return TREATMENT_FIELDS.filter(
    treatmentField =>
      Array.isArray(assessmentForm[treatmentField.key]) &&
      assessmentForm[treatmentField.key].length > 0
  ).map(treatmentField => ({
    label: treatmentField.label,
    control: new FormControl({ value: assessmentForm[treatmentField.key], disabled: true })
  }));
}

export function buildPlanTextRows(assessmentForm: any): PlanTextRow[] {
  if (!assessmentForm) {
    return [];
  }
  return PLAN_TEXT_FIELDS.map(textField => {
    const value = toPlainText(assessmentForm[textField.key]);
    return {
      label: textField.label,
      value: value,
      isLongEnoughToClamp: value.length > VISIBLE_CHARACTERS_THAT_FIT_BEFORE_CLAMPING
    };
  }).filter(planRow => !!planRow.value);
}

export function readSignoffField(signoffValue: any, fieldName: string): string {
  if (!signoffValue) {
    return '';
  }
  const field = signoffValue[fieldName];
  if (field && typeof field === 'object' && 'value' in field) {
    return field.value ? String(field.value) : '';
  }
  return field ? String(field) : '';
}

function countVisibleCharacters(html: string): number {
  const container = document.createElement('div');
  container.innerHTML = html;
  const visibleText = container.textContent || container.innerText || '';
  return visibleText.trim().length;
}

function toPlainText(value: any): string {
  if (!value) {
    return '';
  }
  if (Array.isArray(value)) {
    return value
      .map(item => toTreatmentName(item))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof value === 'object') {
    return toTreatmentName(value);
  }
  return String(value).trim();
}

function toTreatmentName(treatment: any): string {
  if (!treatment) {
    return '';
  }
  if (typeof treatment === 'string') {
    return treatment.trim();
  }
  return treatment.name ? String(treatment.name).trim() : '';
}
